import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Plus, History } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError, streamPost, t } from "@/lib/api";
import { AiText, ModelPicker, PageHeader } from "@/components/Common";

function Chat({ sim, onUpdate }) {
  const [messages, setMessages] = useState(sim.messages || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const started = useRef(false);

  const send = async (msg) => {
    setBusy(true);
    const base = [...messages, ...(msg ? [{ role: "user", content: msg }] : [])];
    setMessages([...base, { role: "assistant", content: "" }]);
    setText("");
    try {
      await streamPost(`/perspectives/${sim.sim_id}/message`, { text: msg }, (full) => setMessages([...base, { role: "assistant", content: full }]));
      onUpdate?.();
    } catch (e) { toast.error(e.message); setMessages(base); }
    setBusy(false);
  };

  useEffect(() => {
    if (!started.current && (sim.messages || []).length === 0) { started.current = true; send(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim.sim_id]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [messages]);

  return (
    <div className="rounded-3xl border border-slate-200 bg-white" data-testid="perspective-chat">
      <div className="border-b border-slate-100 px-6 py-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-rose-600">{sim.mode_label}</div>
        <div className="mt-1 text-sm text-slate-600 line-clamp-2">{sim.project}</div>
      </div>
      <div className="max-h-[480px] overflow-y-auto space-y-4 px-6 py-6">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`chat-message-${m.role}-${i}`}>
            {m.role === "user" ? (
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-indigo-600 px-4 py-2.5 text-sm text-white">{m.content}</div>
            ) : (
              <div className="max-w-[90%] rounded-2xl rounded-bl-sm border border-slate-200 bg-slate-50 px-4 py-3">
                {m.content ? <AiText text={m.content} /> : <span className="text-sm text-slate-400 animate-pulse">{t("L'IA réfléchit…", "The AI is thinking…")}</span>}
              </div>
            )}
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) send(text.trim()); }} className="flex gap-3 border-t border-slate-100 p-4">
        <Textarea data-testid="perspective-reply-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Répondez, défendez-vous, creusez…", "Reply, defend your idea, dig deeper…")} className="resize-none"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (text.trim() && !busy) send(text.trim()); } }} />
        <button data-testid="perspective-reply-send" disabled={busy || !text.trim()} className="self-end rounded-xl bg-indigo-600 p-3 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

export default function Simulator() {
  const [modes, setModes] = useState([]);
  const [project, setProject] = useState("");
  const [mode, setMode] = useState("concurrent");
  const [model, setModel] = useState("claude");
  const [sim, setSim] = useState(null);
  const [history, setHistory] = useState([]);
  const loadHistory = () => api.get("/perspectives").then(({ data }) => setHistory(data));
  useEffect(() => { api.get("/perspectives/modes").then(({ data }) => setModes(data)); loadHistory(); }, []);

  const start = async () => {
    try { const { data } = await api.post("/perspectives", { project, mode, model }); setSim(data); toast.success(t("Simulation lancée (+20 pts)", "Simulation started (+20 pts)")); loadHistory(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const open = async (id) => { const { data } = await api.get(`/perspectives/${id}`); setSim(data); };

  return (
    <div>
      <PageHeader eyebrow={t("Changer d'angle", "Change your angle")} title={t("Simulateur de perspectives", "Perspective simulator")} subtitle={t("Saisissez un projet ou une décision. L'IA endosse un point de vue inattendu et vous pousse à approfondir — sans jamais répondre à votre place.", "Enter a project or decision. The AI takes on an unexpected point of view and pushes you to dig deeper — without ever answering for you.")}>
        <ModelPicker value={model} onChange={setModel} />
      </PageHeader>
      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-2 space-y-6">
          {sim ? (
            <button data-testid="perspective-new-button" onClick={() => { setSim(null); setProject(""); }} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold hover:border-indigo-300 transition-colors"><Plus className="h-4 w-4" /> {t("Nouvelle simulation", "New simulation")}</button>
          ) : (
            <>
              <div>
                <label className="text-sm font-semibold">{t("Votre projet ou décision", "Your project or decision")}</label>
                <Textarea data-testid="perspective-input-project" rows={5} value={project} onChange={(e) => setProject(e.target.value)} className="mt-2 bg-white" placeholder={t("Ex. : Nous voulons lancer une offre d'abonnement pour nos clients PME…", "E.g. We want to launch a subscription offer for our SME customers…")} />
              </div>
              <div className="space-y-2" data-testid="perspective-select-mode">
                {modes.map((m) => (
                  <button key={m.id} data-testid={`perspective-mode-${m.id}`} onClick={() => setMode(m.id)}
                    className={`w-full rounded-2xl border p-4 text-left transition-colors ${mode === m.id ? "border-indigo-600 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-200"}`}>
                    <div className="font-semibold text-sm">{m.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">“{m.question}”</div>
                  </button>
                ))}
              </div>
              <button data-testid="perspective-submit-button" disabled={project.trim().length < 3} onClick={start} className="w-full rounded-full bg-indigo-600 py-3 font-semibold text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors">{t("Lancer la simulation", "Start simulation")}</button>
            </>
          )}
          {history.length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400"><History className="h-3.5 w-3.5" /> {t("Mes simulations", "My simulations")}</div>
              <div className="mt-3 space-y-1.5">
                {history.slice(0, 6).map((h) => (
                  <button key={h.sim_id} onClick={() => open(h.sim_id)} data-testid={`perspective-history-${h.sim_id}`} className="block w-full truncate rounded-lg px-3 py-2 text-left text-sm hover:bg-white transition-colors">
                    <span className="text-rose-600 font-medium">{h.mode_label}</span> · {h.project}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-3">
          {sim ? <Chat key={sim.sim_id} sim={sim} /> : (
            <div className="relative h-full min-h-[360px] overflow-hidden rounded-3xl">
              <img src="https://images.pexels.com/photos/39205127/pexels-photo-39205127.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-slate-900/60" />
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <div className="font-mono text-xs text-amber-300">{t("CO-PENSEUR, PAS ORACLE", "THINKING PARTNER, NOT ORACLE")}</div>
                <p className="mt-2 font-display text-2xl font-semibold">{t("Les meilleures idées survivent aux meilleures objections.", "The best ideas survive the best objections.")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
